import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Gamepad2, Loader2, Plus, Radio, Swords, Trophy, Video } from 'lucide-react';
import { gameshowApi, type Show, type ShowFormat } from '@/services/gameshowApi';
import { useToast } from '@/hooks/use-toast';

// ── /my-shows ─────────────────────────────────────────────────────────
// Minimal utility surface: list the host's game shows + create a new one.
// Dark game-show theme, Hey Joe tokens. Unlinked (direct-URL only).

const PAGE_BG = 'radial-gradient(130% 90% at 50% -8%, #241b30 0%, #16110f 46%, #0c0908 100%)';
const GOLD = 'var(--hj-yellow)';
const GREEN = 'var(--hj-green)';
const PINK = 'var(--hj-pink)';

const statusMeta: Record<Show['status'], { label: string; color: string }> = {
  lobby: { label: 'Lobby', color: GOLD },
  live: { label: 'Live', color: '#ff4d6a' },
  ended: { label: 'Ended', color: 'rgba(255,255,255,.5)' },
};

const GameShowsPage: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [shows, setShows] = useState<Show[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const [title, setTitle] = useState('');
  const [format, setFormat] = useState<ShowFormat>('quiz');

  useEffect(() => {
    let alive = true;
    gameshowApi
      .myShows()
      .then((s) => alive && setShows(s))
      .catch((e) => alive && setError(e instanceof Error ? e.message : 'Could not load your shows.'));
    return () => {
      alive = false;
    };
  }, []);

  const create = async () => {
    const t = title.trim();
    if (!t) {
      toast({ title: 'Give your show a title', variant: 'destructive' });
      return;
    }
    setCreating(true);
    try {
      const show = await gameshowApi.createShow(t, format);
      navigate(`/show-host/${show._id}`);
    } catch (e) {
      toast({
        title: 'Could not create show',
        description: e instanceof Error ? e.message : undefined,
        variant: 'destructive',
      });
      setCreating(false);
    }
  };

  const formats: { key: ShowFormat; label: string; blurb: string; Icon: typeof Trophy }[] = [
    { key: 'quiz', label: 'Quiz show', blurb: 'Host asks, everyone answers, leaderboard climbs.', Icon: Trophy },
    { key: 'battle', label: 'Video battle', blurb: 'Head-to-head performances — the audience votes.', Icon: Swords },
  ];

  return (
    <div
      style={{
        minHeight: '100vh',
        background: PAGE_BG,
        color: 'var(--hj-ink)',
        fontFamily: 'var(--hj-font-ui)',
        padding: '48px 24px 80px',
      }}
    >
      <div style={{ maxWidth: 880, margin: '0 auto' }}>
        <header style={{ margin: '0 0 28px', padding: '22px 24px 20px', borderRadius: 'var(--hj-r-md)', background: 'var(--hj-beige)', border: '1px solid var(--hj-beige-line)', color: 'var(--hj-beige-ink)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            <Gamepad2 size={20} color="var(--hj-beige-ink-2)" />
            <span className="mono-label" style={{ color: 'var(--hj-beige-ink-2)' }}>
              Hey Joe · Game shows
            </span>
          </div>
          <h1 className="font-hj-display m-0" style={{ fontSize: 40, letterSpacing: '-0.02em', color: 'var(--hj-beige-ink)' }}>
            Your game shows
          </h1>
        </header>

        {/* New show card */}
        <div
          style={{
            border: '1px solid rgba(255,255,255,.1)',
            background: 'rgba(255,255,255,.04)',
            borderRadius: 18,
            padding: 22,
            marginBottom: 34,
          }}
        >
          <div className="mono-label" style={{ color: 'rgba(255,255,255,.5)', marginBottom: 12 }}>
            New game show
          </div>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Thursday Night Live"
            onKeyDown={(e) => e.key === 'Enter' && create()}
            style={{
              width: '100%',
              boxSizing: 'border-box',
              background: 'rgba(0,0,0,.28)',
              border: '1px solid rgba(255,255,255,.14)',
              borderRadius: 12,
              padding: '13px 15px',
              color: 'var(--hj-ink)',
              fontFamily: 'var(--hj-font-ui)',
              fontSize: 16,
              fontWeight: 600,
              marginBottom: 16,
              outline: 'none',
            }}
          />

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 18 }}>
            {formats.map(({ key, label, blurb, Icon }) => {
              const active = format === key;
              return (
                <button
                  key={key}
                  onClick={() => setFormat(key)}
                  style={{
                    textAlign: 'left',
                    cursor: 'pointer',
                    border: `1.5px solid ${active ? 'rgba(255,214,0,.55)' : 'rgba(255,255,255,.12)'}`,
                    background: active ? 'rgba(255,214,0,.09)' : 'rgba(255,255,255,.02)',
                    borderRadius: 14,
                    padding: 16,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8,
                    transition: 'all .15s',
                  }}
                >
                  <Icon size={20} color={active ? GOLD : 'rgba(255,255,255,.7)'} />
                  <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--hj-ink)' }}>{label}</span>
                  <span style={{ fontSize: 12.5, lineHeight: 1.4, color: 'rgba(255,255,255,.62)' }}>{blurb}</span>
                </button>
              );
            })}
          </div>

          <button
            onClick={create}
            disabled={creating}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              cursor: creating ? 'default' : 'pointer',
              border: 'none',
              borderRadius: 13,
              padding: '13px 22px',
              fontFamily: 'var(--hj-font-ui)',
              fontWeight: 700,
              fontSize: 15,
              color: '#150e20',
              background: 'linear-gradient(90deg,#ffe566,#ffd600)',
              opacity: creating ? 0.7 : 1,
            }}
          >
            {creating ? <Loader2 size={17} className="animate-spin" /> : <Plus size={17} />}
            Create show
          </button>
        </div>

        {/* Existing shows */}
        <div className="mono-label" style={{ color: 'rgba(255,255,255,.5)', marginBottom: 12 }}>
          Your shows
        </div>

        {error && (
          <div style={{ color: '#ff9db3', fontSize: 14, padding: '18px 0' }}>{error}</div>
        )}

        {!error && shows === null && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'rgba(255,255,255,.6)', padding: '18px 0' }}>
            <Loader2 size={18} className="animate-spin" /> Loading…
          </div>
        )}

        {!error && shows && shows.length === 0 && (
          <div
            style={{
              border: '1px dashed rgba(255,255,255,.16)',
              borderRadius: 14,
              padding: 28,
              textAlign: 'center',
              color: 'rgba(255,255,255,.6)',
              fontSize: 14,
            }}
          >
            No shows yet — create one above to get started.
          </div>
        )}

        {!error && shows && shows.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {shows.map((s) => {
              const sm = statusMeta[s.status];
              return (
                <button
                  key={s._id}
                  onClick={() => navigate(`/show-host/${s._id}`)}
                  style={{
                    textAlign: 'left',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 14,
                    border: '1px solid rgba(255,255,255,.1)',
                    background: 'rgba(255,255,255,.03)',
                    borderRadius: 14,
                    padding: '15px 17px',
                    transition: 'all .15s',
                  }}
                >
                  <span
                    style={{
                      width: 40,
                      height: 40,
                      flexShrink: 0,
                      borderRadius: 11,
                      background: s.format === 'battle' ? 'var(--hj-pink-tint)' : 'var(--hj-yellow-tint)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {s.format === 'battle' ? <Swords size={20} color={PINK} /> : <Trophy size={20} color={GOLD} />}
                  </span>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: 'block', fontSize: 16, fontWeight: 700, color: 'var(--hj-ink)' }}>
                      {s.title}
                    </span>
                    <span style={{ display: 'block', fontSize: 12.5, color: 'rgba(255,255,255,.55)', marginTop: 2 }}>
                      {s.format === 'battle' ? 'Video battle' : 'Quiz show'}
                    </span>
                  </span>
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                      color: sm.color,
                    }}
                  >
                    {s.status === 'live' && (
                      <span
                        style={{ width: 8, height: 8, borderRadius: '50%', background: '#ff4d6a' }}
                      />
                    )}
                    {sm.label}
                  </span>
                  <Radio size={16} color="rgba(255,255,255,.3)" />
                </button>
              );
            })}
          </div>
        )}

        <div style={{ marginTop: 40, display: 'flex', alignItems: 'center', gap: 8, color: 'rgba(255,255,255,.4)', fontSize: 12 }}>
          <Video size={14} /> Runs from your laptop — go live as usual, drive the show from the host panel.
        </div>
      </div>
    </div>
  );
};

export default GameShowsPage;
